/**
 * 独享容器文件管理（文件通道 WebSocket）。用户与后台共用：传入 fetchTicket 与起始目录。
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Breadcrumb, Button, Checkbox, Dropdown, Form, Input, Modal, Progress, Select, Space, Table, Tooltip, Typography,
    message,
} from 'antd';
import {
    CloudDownloadOutlined, CloseOutlined, FileOutlined, FolderAddOutlined, FolderFilled, LinkOutlined, MoreOutlined,
    ReloadOutlined, UploadOutlined, HomeOutlined,
} from '@ant-design/icons';
import { useTranslation } from '../../i18n';
import { useFilesSocket, b64ToBytes, bytesToB64 } from './filesSocket';
import { SocketBar } from './ContainerTools';
import { fmtBytes, arr } from './ContainerParts';

const { Text } = Typography;
const CHUNK = 512 * 1024;
const EDIT_MAX = 2 * 1024 * 1024;
const WARN_DL = 200 * 1024 * 1024;
const ARCHIVE_RE = /\.(zip|tar\.gz|tgz)$/i;

const join = (dir, name) => (dir === '/' ? `/${name}` : `${dir.replace(/\/+$/, '')}/${name}`);
const parentOf = (p) => { const i = p.replace(/\/+$/, '').lastIndexOf('/'); return i <= 0 ? '/' : p.slice(0, i); };
const baseName = (p) => p.replace(/\/+$/, '').split('/').pop() || '';
const normPath = (p) => { const s = `/${String(p || '/').replace(/^\/+/, '')}`; return s.length > 1 ? s.replace(/\/+$/, '') : '/'; };
const fmtTime = (v) => {
    const n = Number(v);
    if (!v) return '-';
    const d = Number.isFinite(n) ? new Date(n < 1e12 ? n * 1000 : n) : new Date(v);
    return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
};
const saveBlob = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
};

const NameField = ({ t }) => (
    <Form.Item name="name" label={t('Name')} rules={[{ required: true, whitespace: true, message: t('Please enter a name') },
        { pattern: /^[^/]+$/, message: t('Name cannot contain /') }]}>
        <Input autoFocus />
    </Form.Item>
);

const ContainerFiles = ({ fetchTicket, root }) => {
    const { t } = useTranslation();
    const home = normPath(root || '/');
    const { sockRef, state, error, connect } = useFilesSocket(fetchTicket);
    const ready = state === 'open';
    const [cwd, setCwd] = useState(home);
    const [entries, setEntries] = useState([]);
    const [truncated, setTruncated] = useState(false);
    const [loading, setLoading] = useState(false);
    const [df, setDf] = useState(null);
    const [selected, setSelected] = useState([]);
    const [dialog, setDialog] = useState(null); // {type, rows}
    const [busy, setBusy] = useState(false);
    const [uploads, setUploads] = useState([]); // {path, file, sent, status, error}
    const [jobs, setJobs] = useState([]);
    const [dl, setDl] = useState(null); // {name, got, size}
    const [editor, setEditor] = useState(null); // {path, text}
    const [form] = Form.useForm();
    const fileInput = useRef(null);
    const cwdRef = useRef(cwd);
    cwdRef.current = cwd;

    const sock = () => sockRef.current;

    const ls = useCallback(async (path) => {
        const s = sockRef.current;
        if (!s) return;
        setLoading(true);
        try {
            const d = await s.call('ls', { path });
            const list = arr(d.entries).slice().sort((a, b) => ((b.type === 'd') - (a.type === 'd')) || String(a.name).localeCompare(String(b.name)));
            setEntries(list);
            setTruncated(!!d.truncated);
            setCwd(d.path || path);
            setSelected([]);
        } catch (e) { message.error(e.message); }
        setLoading(false);
        s.call('df').then(setDf).catch(() => {});
    }, [sockRef]);

    useEffect(() => { if (ready) ls(cwdRef.current); }, [ready, ls]);
    const refresh = () => ls(cwd);

    // ---- jobs ----
    useEffect(() => {
        const running = jobs.filter((j) => !['done', 'failed'].includes(j.state));
        if (!running.length || !ready) return undefined;
        const tm = setTimeout(async () => {
            const s = sockRef.current;
            if (!s) return;
            const next = await Promise.all(jobs.map(async (j) => {
                if (['done', 'failed'].includes(j.state)) return j;
                try {
                    const d = await s.call('job_status', { job: j.id });
                    const nj = { ...j, ...(d.job || d) };
                    if (nj.state === 'done') ls(cwdRef.current);
                    return nj;
                } catch (e) { return { ...j, state: 'failed', error: e.message }; }
            }));
            setJobs(next);
        }, 2000);
        return () => clearTimeout(tm);
    }, [jobs, ready, ls, sockRef]);

    const startJob = async (args, label) => {
        const d = await sock().call('job_start', args);
        const job = d.job || {};
        setJobs((js) => [...js, { ...job, label }]);
    };

    // ---- upload ----
    const patchUpload = (path, p) => setUploads((us) => us.map((u) => (u.path === path ? { ...u, ...p } : u)));
    const runUpload = async (item) => {
        let s = sockRef.current;
        if (!s || !s.isOpen) s = await connect();
        if (!s) { patchUpload(item.path, { status: 'failed', error: t('Disconnected') }); return; }
        patchUpload(item.path, { status: 'uploading', error: '' });
        try {
            const st = await s.call('upload_stat', { path: item.path });
            let off = st.exists ? Number(st.entry?.size) || 0 : 0;
            if (off > item.file.size) off = 0;
            patchUpload(item.path, { sent: off });
            while (off < item.file.size) {
                const len = Math.min(CHUNK, item.file.size - off);
                // eslint-disable-next-line no-await-in-loop
                const buf = await item.file.slice(off, off + len).arrayBuffer();
                // eslint-disable-next-line no-await-in-loop
                await s.call('upload_chunk', { path: item.path, offset: off, len }, buf);
                off += len;
                patchUpload(item.path, { sent: off });
            }
            await s.call('upload_finish', { path: item.path });
            patchUpload(item.path, { status: 'done' });
            if (parentOf(item.path) === cwdRef.current) ls(cwdRef.current);
        } catch (e) {
            patchUpload(item.path, { status: 'failed', error: e.message });
        }
    };
    const onPickFiles = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        const items = files.map((f) => ({ path: join(cwd, f.name), file: f, sent: 0, status: 'queued', error: '' }));
        setUploads((us) => [...us.filter((u) => !items.some((i) => i.path === u.path)), ...items]);
        for (const it of items) {
            // eslint-disable-next-line no-await-in-loop
            await runUpload(it);
        }
    };

    // ---- download ----
    const download = (row) => {
        const path = join(cwd, row.name);
        const go = async () => {
            setDl({ name: row.name, got: 0, size: Number(row.size) || 0 });
            try {
                const blob = await sock().download(path, (got, size) => setDl({ name: row.name, got, size }));
                saveBlob(blob, row.name);
            } catch (e) { message.error(e.message); }
            setDl(null);
        };
        if (Number(row.size) > WARN_DL) {
            Modal.confirm({ title: t('Large file ({{size}}); the browser download may be slow. Continue?', { size: fmtBytes(row.size) }), onOk: go });
        } else go();
    };

    // ---- editor ----
    const openEditor = async (row) => {
        const path = join(cwd, row.name);
        try {
            const d = await sock().call('read', { path });
            const text = new TextDecoder('utf-8').decode(b64ToBytes(d.content));
            setEditor({ path, text, saving: false });
        } catch (e) { message.error(e.message); }
    };
    const saveEditor = async () => {
        setEditor((ed) => ({ ...ed, saving: true }));
        try {
            await sock().call('write', { path: editor.path, content: bytesToB64(new TextEncoder().encode(editor.text)) });
            message.success(t('Saved'));
            setEditor(null);
            refresh();
        } catch (e) {
            message.error(e.message);
            setEditor((ed) => ed && { ...ed, saving: false });
        }
    };

    // ---- delete ----
    const remove = (rows) => {
        Modal.confirm({
            title: rows.length > 1 ? t('Delete the {{n}} selected items?', { n: rows.length }) : t('Delete {{name}}?', { name: rows[0].name }),
            okType: 'danger',
            okText: t('Delete'),
            onOk: async () => {
                try {
                    for (const r of rows) {
                        // eslint-disable-next-line no-await-in-loop
                        await sock().call('rm', { path: join(cwd, r.name) });
                    }
                    message.success(t('Deleted'));
                } catch (e) { message.error(e.message); }
                refresh();
            },
        });
    };

    // ---- dialogs ----
    const openDialog = (type, rows = []) => {
        const r = rows[0];
        const init = {
            mkdir: {},
            rename: { name: r?.name },
            copy: { dest: r && join(cwd, `${r.name}.copy`) },
            chmod: { mode: r ? String(r.mode || '').replace(/^0(?=\d{3,4}$)/, '') : '', recursive: false },
            chown: { owner: r ? `${r.uid}:${r.gid}` : '', recursive: false },
            compress: { name: `${rows.length === 1 ? r.name : baseName(cwd) || 'archive'}.tar.gz`, format: 'tar.gz' },
            extract: { dest: cwd },
            fetch: { url: '', name: '' },
        }[type];
        form.resetFields();
        form.setFieldsValue(init);
        setDialog({ type, rows });
    };
    const submitDialog = async () => {
        const v = await form.validateFields();
        const { type, rows } = dialog;
        const r = rows[0];
        const p = r && join(cwd, r.name);
        setBusy(true);
        try {
            const s = sock();
            if (type === 'mkdir') await s.call('mkdir', { path: join(cwd, v.name.trim()) });
            if (type === 'rename') await s.call('mv', { path: p, dest: join(cwd, v.name.trim()) });
            if (type === 'copy') await s.call('cp', { path: p, dest: v.dest.trim() });
            if (type === 'chmod') await s.call('chmod', { path: p, mode: v.mode.trim(), recursive: !!v.recursive });
            if (type === 'chown') await s.call('chown', { path: p, owner: v.owner.trim(), recursive: !!v.recursive });
            if (type === 'compress') {
                await startJob({
                    kind: 'compress', format: v.format, path: join(cwd, v.name.trim()), sources: rows.map((x) => join(cwd, x.name)),
                }, `${t('Compress')} ${v.name.trim()}`);
            }
            if (type === 'extract') await startJob({ kind: 'extract', path: p, dest: v.dest.trim() }, `${t('Extract')} ${r.name}`);
            if (type === 'fetch') {
                const name = (v.name || '').trim() || baseName(v.url.trim().split(/[?#]/)[0]) || 'download';
                await startJob({ kind: 'fetch', url: v.url.trim(), path: join(cwd, name) }, `${t('Remote download')} ${name}`);
            }
            setDialog(null);
            if (!['compress', 'extract', 'fetch'].includes(type)) { message.success(t('Operation successful')); refresh(); }
        } catch (e) { message.error(e.message); }
        setBusy(false);
    };

    const onFormatChange = (f) => {
        const n = String(form.getFieldValue('name') || '').replace(/\.(zip|tar\.gz|tgz)$/i, '');
        form.setFieldsValue({ name: `${n}.${f}` });
    };

    const DIALOG_TITLE = {
        mkdir: t('Create folder'), rename: t('Rename'), copy: t('Copy'), chmod: t('Permissions'), chown: t('Owner'),
        compress: t('Compress'), extract: t('Extract'), fetch: t('Remote download'),
    };

    const rowMenu = (row) => {
        const isFile = row.type === 'f';
        const items = [
            isFile && { key: 'download', label: t('Download') },
            isFile && Number(row.size) < EDIT_MAX && { key: 'edit', label: t('Edit') },
            { key: 'rename', label: t('Rename') },
            { key: 'copy', label: t('Copy') },
            { key: 'compress', label: t('Compress') },
            isFile && ARCHIVE_RE.test(row.name) && { key: 'extract', label: t('Extract') },
            { key: 'chmod', label: t('Permissions') },
            { key: 'chown', label: t('Owner') },
            { type: 'divider' },
            { key: 'rm', label: t('Delete'), danger: true },
        ].filter(Boolean);
        const onClick = ({ key }) => {
            if (key === 'download') download(row);
            else if (key === 'edit') openEditor(row);
            else if (key === 'rm') remove([row]);
            else openDialog(key, [row]);
        };
        return { items, onClick };
    };

    const enter = (row) => {
        if (row.type === 'd' || row.type === 'l') ls(join(cwd, row.name));
        else if (row.type === 'f' && Number(row.size) < EDIT_MAX) openEditor(row);
    };

    const columns = [
        {
            title: t('Name'), dataIndex: 'name', render: (v, row) => (
                <Space size={6}>
                    {row.type === 'd' ? <FolderFilled style={{ color: '#f7c948' }} /> : row.type === 'l' ? <LinkOutlined /> : <FileOutlined />}
                    <Button type="link" size="small" style={{ padding: 0, height: 'auto' }} onClick={() => enter(row)}>{v}</Button>
                    {row.type === 'l' && row.link && <Text type="secondary" style={{ fontSize: 12 }}>→ {row.link}</Text>}
                </Space>
            ),
        },
        { title: t('Size'), dataIndex: 'size', width: 100, render: (v, row) => (row.type === 'd' ? '-' : fmtBytes(v)) },
        { title: t('Permissions'), dataIndex: 'mode', width: 80, render: (v) => <Text code>{v}</Text> },
        { title: t('Owner'), key: 'own', width: 100, render: (_, r) => `${r.uid}:${r.gid}` },
        { title: t('Modified at'), dataIndex: 'mtime', width: 170, render: fmtTime },
        {
            title: '', key: 'op', width: 48, render: (_, row) => (
                <Dropdown menu={rowMenu(row)} trigger={['click']}><Button size="small" type="text" icon={<MoreOutlined />} /></Dropdown>
            ),
        },
    ];

    const parts = cwd.split('/').filter(Boolean);
    const crumbs = [
        { title: <Typography.Link onClick={() => ls('/')}>/</Typography.Link> },
        ...parts.map((p, i) => {
            const to = `/${parts.slice(0, i + 1).join('/')}`;
            return { title: i === parts.length - 1 ? p : <Typography.Link onClick={() => ls(to)}>{p}</Typography.Link> };
        }),
    ];
    const selRows = entries.filter((e) => selected.includes(e.name));
    const activeUploads = uploads.filter((u) => u.status !== 'done');

    return (
        <div>
            <SocketBar state={state} error={error} onReconnect={connect} />
            <Space wrap style={{ marginBottom: 8, width: '100%', justifyContent: 'space-between' }}>
                <Space wrap size={6}>
                    <Tooltip title={home}><Button size="small" icon={<HomeOutlined />} disabled={!ready} onClick={() => ls(home)} /></Tooltip>
                    <Button size="small" icon={<ReloadOutlined />} disabled={!ready} onClick={refresh} />
                    <Button size="small" type="primary" icon={<UploadOutlined />} disabled={!ready} onClick={() => fileInput.current?.click()}>{t('Upload')}</Button>
                    <Button size="small" icon={<FolderAddOutlined />} disabled={!ready} onClick={() => openDialog('mkdir')}>{t('Create folder')}</Button>
                    <Button size="small" icon={<CloudDownloadOutlined />} disabled={!ready} onClick={() => openDialog('fetch')}>{t('Remote download')}</Button>
                    {selRows.length > 0 && <>
                        <Button size="small" onClick={() => openDialog('compress', selRows)}>{t('Compress')} ({selRows.length})</Button>
                        <Button size="small" danger onClick={() => remove(selRows)}>{t('Delete')} ({selRows.length})</Button>
                    </>}
                </Space>
                {df && <Text type="secondary" style={{ fontSize: 12 }}>{t('{{free}} free / {{total}} total', { free: fmtBytes(df.free), total: fmtBytes(df.total) })}</Text>}
            </Space>
            <input ref={fileInput} type="file" multiple hidden onChange={onPickFiles} />
            <Breadcrumb style={{ marginBottom: 8 }} items={crumbs} />

            {(activeUploads.length > 0 || dl || jobs.length > 0) && (
                <Space direction="vertical" size={4} style={{ width: '100%', marginBottom: 8 }}>
                    {activeUploads.map((u) => (
                        <Space key={u.path} style={{ width: '100%' }} size={8}>
                            <UploadOutlined />
                            <Text ellipsis style={{ maxWidth: 220 }}>{baseName(u.path)}</Text>
                            <Progress size="small" style={{ width: 200, margin: 0 }} status={u.status === 'failed' ? 'exception' : 'active'}
                                percent={u.file.size ? Math.floor((u.sent / u.file.size) * 100) : 100} />
                            {u.status === 'failed' && <>
                                <Text type="danger" style={{ fontSize: 12 }}>{u.error}</Text>
                                <Button size="small" type="link" disabled={state === 'connecting'} onClick={() => runUpload(u)}>{t('Resume upload')}</Button>
                                <Button size="small" type="text" icon={<CloseOutlined />} onClick={() => setUploads((us) => us.filter((x) => x.path !== u.path))} />
                            </>}
                        </Space>
                    ))}
                    {dl && (
                        <Space size={8}>
                            <CloudDownloadOutlined />
                            <Text ellipsis style={{ maxWidth: 220 }}>{dl.name}</Text>
                            <Progress size="small" style={{ width: 200, margin: 0 }} status="active"
                                percent={dl.size ? Math.floor((dl.got / dl.size) * 100) : 0} />
                        </Space>
                    )}
                    {jobs.map((j) => (
                        <Space key={j.id} size={8}>
                            <Text style={{ fontSize: 12 }}>{j.label}</Text>
                            <Progress size="small" style={{ width: 200, margin: 0 }}
                                status={j.state === 'failed' ? 'exception' : j.state === 'done' ? 'success' : 'active'}
                                percent={j.state === 'done' ? 100 : (Number(j.total) ? Math.floor((Number(j.bytes) / Number(j.total)) * 100) : 0)}
                                format={(pc) => (Number(j.total) || j.state === 'done' ? `${pc}%` : fmtBytes(j.bytes))} />
                            {j.state === 'failed' && <Text type="danger" style={{ fontSize: 12 }}>{j.error || t('Failed')}</Text>}
                            {['done', 'failed'].includes(j.state) && (
                                <Button size="small" type="text" icon={<CloseOutlined />} onClick={() => setJobs((js) => js.filter((x) => x.id !== j.id))} />
                            )}
                        </Space>
                    ))}
                </Space>
            )}

            <Table size="small" rowKey="name" columns={columns} dataSource={entries} loading={loading} pagination={false}
                scroll={{ x: 640, y: 480 }}
                rowSelection={{ selectedRowKeys: selected, onChange: (k) => setSelected(k) }}
                onRow={(row) => ({ onDoubleClick: () => enter(row) })}
                footer={truncated ? () => <Text type="secondary">{t('Too many entries; showing only some')}</Text> : undefined}
                locale={{ emptyText: ready ? t('Empty folder') : ' ' }}
                title={cwd !== '/' ? () => <Typography.Link onClick={() => ls(parentOf(cwd))}>..</Typography.Link> : undefined} />

            <Modal open={!!dialog} title={dialog && DIALOG_TITLE[dialog.type]} onCancel={() => setDialog(null)} onOk={submitDialog}
                confirmLoading={busy} forceRender>
                <Form form={form} layout="vertical">
                    {dialog && ['mkdir', 'rename'].includes(dialog.type) && <NameField t={t} />}
                    {dialog?.type === 'copy' && (
                        <Form.Item name="dest" label={t('Destination path')} rules={[{ required: true, whitespace: true }]}><Input /></Form.Item>
                    )}
                    {dialog?.type === 'chmod' && (
                        <Form.Item name="mode" label={t('Permissions')} rules={[{ required: true, pattern: /^[0-7]{3,4}$/, message: t('e.g. 755') }]}>
                            <Input placeholder="755" style={{ width: 120 }} />
                        </Form.Item>
                    )}
                    {dialog?.type === 'chown' && (
                        <Form.Item name="owner" label={t('Owner')} rules={[{ required: true, pattern: /^\d+:\d+$/, message: 'uid:gid' }]}>
                            <Input placeholder="1000:1000" style={{ width: 160 }} />
                        </Form.Item>
                    )}
                    {dialog && ['chmod', 'chown'].includes(dialog.type) && dialog.rows[0]?.type === 'd' && (
                        <Form.Item name="recursive" valuePropName="checked"><Checkbox>{t('Include subdirectories')}</Checkbox></Form.Item>
                    )}
                    {dialog?.type === 'compress' && <>
                        <Form.Item name="format" label={t('Format')}>
                            <Select style={{ width: 120 }} onChange={onFormatChange} options={[{ value: 'tar.gz', label: 'tar.gz' }, { value: 'zip', label: 'zip' }]} />
                        </Form.Item>
                        <NameField t={t} />
                    </>}
                    {dialog?.type === 'extract' && (
                        <Form.Item name="dest" label={t('Extract to')} rules={[{ required: true, whitespace: true }]}><Input /></Form.Item>
                    )}
                    {dialog?.type === 'fetch' && <>
                        <Form.Item name="url" label="URL" rules={[{ required: true, type: 'url', message: t('Enter a valid URL') }]}>
                            <Input placeholder="https://" autoFocus />
                        </Form.Item>
                        <Form.Item name="name" label={t('Save as')}><Input placeholder={t('Defaults to the URL file name')} /></Form.Item>
                    </>}
                </Form>
            </Modal>

            <Modal open={!!editor} title={editor?.path} width={900} onCancel={() => setEditor(null)} onOk={saveEditor}
                okText={t('Save')} confirmLoading={!!editor?.saving} destroyOnClose maskClosable={false}>
                <Input.TextArea value={editor?.text} onChange={(e) => setEditor((ed) => ({ ...ed, text: e.target.value }))}
                    spellCheck={false} autoSize={{ minRows: 18, maxRows: 30 }}
                    style={{ fontFamily: 'Menlo, Consolas, monospace', fontSize: 12 }} />
            </Modal>
        </div>
    );
};

export default ContainerFiles;
